import React, { useState, useRef, useEffect } from 'react';
import { businessMetrics, formatters } from '../../config/businessMetrics';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [heights, setHeights] = useState({});
  const contentRefs = useRef({});

  const faqs = [
    {
      question: 'How do you maintain confidentiality during the sale process?',
      answer:
        "Confidentiality is paramount. We use code names for all initial outreach, require NDAs before sharing any identifying information, and only approach buyers who have demonstrated genuine acquisition interest. Your employees, customers, and competitors won't know you're exploring a sale until you're ready to tell them.",
    },
    {
      question: 'What size businesses do you work with?',
      answer:
        `We specialize in businesses with ${businessMetrics.businessSize.revenueRange.displayText} in annual revenue. This is the sweet spot where founders can achieve life-changing exits, but often get underserved by traditional investment banks who focus on larger deals. We've built our entire process around maximizing value for businesses in this range.`,
    },
    {
      question: 'How long does the sale process typically take?',
      answer:
        `Our process typically takes ${businessMetrics.timeline.fullProcess.displayText} from start to finish. This includes initial valuation, buyer outreach, negotiation, due diligence, and closing. We've refined our process to be faster than traditional M&A advisors (who often take 12-18 months) while still thorough enough to maximize value.`,
    },
    {
      question: 'Can I stay involved with the business after the sale?',
      answer:
        "Absolutely. Many buyers prefer founders to stay involved, at least during a transition period. We've structured deals where founders keep 10-30% equity, remain as CEO, or transition to a board role. Some founders exit completely, others stay for years. We'll help you find the right buyer who aligns with your personal goals.",
    },
    {
      question: 'How do you value my business?',
      answer:
        'We use multiple valuation methods including EBITDA multiples, revenue multiples, and comparable transaction analysis. But valuation is both art and science - we factor in your growth trajectory, recurring revenue, market position, and strategic value to buyers. Our goal is to position your business to achieve the highest possible valuation through competitive bidding.',
    },
    {
      question: "What if I'm not ready to sell right now?",
      answer:
        "That's perfectly fine. Many of our best outcomes come from founders who start the conversation 12-18 months before they're ready to sell. We can help you understand your current valuation, identify value drivers to focus on, and create a roadmap to maximize your exit when you are ready. There's no obligation to move forward until you're comfortable.",
    },
    {
      question: 'How much do you charge?',
      answer:
        'We work on a success-fee basis - we get paid when you successfully close a transaction. Our fee is a percentage of the transaction value, typically with a minimum fee. This aligns our incentives with yours: the more we get for your business, the more we earn.',
    },
    {
      question: 'What industries do you specialize in?',
      answer:
        'We have deep expertise in Technology (SaaS, IT services, digital agencies), Healthcare (medical practices, dental groups, healthcare services), and Service businesses (commercial services, home services, professional services). Our team has specific industry experience in these sectors, which means we understand your business model and know the right buyers.',
    },
    {
      question: 'How many potential buyers will you contact?',
      answer:
        `We have access to nearly ${businessMetrics.buyers.qualifiedText} from our proprietary database. This includes strategic acquirers, private equity firms, family offices, and other buyers actively looking in your space. The key is quality, not just quantity - we target buyers who understand your business model and can pay premium valuations.`,
    },
    {
      question: 'What makes AMIVA different from other M&A advisors?',
      answer:
        `Three things: Speed (${businessMetrics.timeline.fullProcess.displayText} process vs 12-18 months), Transparency (real-time updates through AMIVA+ platform), and Experience (we're entrepreneurs who have actually built and sold businesses). We're not investment bankers reading from a playbook - we've been in your shoes.`,
    },
    {
      question: 'What if I receive an unsolicited offer?',
      answer:
        `Great! But don't sign anything yet. A single offer, even a good one, leaves money on the table. We can help you evaluate the offer, then quickly bring in competing buyers to create a bidding environment. Our clients typically see ${businessMetrics.performance.higherValuations.displayText} ${businessMetrics.performance.higherValuations.context}.`,
    },
    {
      question: "Do you work with businesses that aren't profitable yet?",
      answer:
        "While we occasionally work with high-growth companies that aren't yet profitable, our sweet spot is businesses with positive EBITDA. Profitable businesses attract more buyers and command better valuations. If you're not profitable yet, we can discuss strategies to get there before going to market.",
    },
  ];

  // Calculate heights for smooth animations
  useEffect(() => {
    const newHeights = {};
    faqs.forEach((_, index) => {
      if (contentRefs.current[index]) {
        newHeights[index] = contentRefs.current[index].scrollHeight;
      }
    });
    setHeights(newHeights);
  }, [faqs]);

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 to-black py-24 lg:py-32 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-amiva-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-amiva-accent rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="bg-white/10 backdrop-blur-sm text-white text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center shadow-lg">
              <span className="w-2 h-2 bg-amiva-accent rounded-full mr-2.5 animate-pulse"></span>
              Frequently Asked Questions
            </span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Common <span className="text-gradient">Questions</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
            Everything you need to know about selling your business with AMIVA.
          </p>
        </div>
      </div>

      {/* Enhanced FAQ Section with Smooth Animations */}
      <div className="py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`group bg-gradient-to-br from-gray-50 to-white rounded-2xl border-2 overflow-hidden transition-all duration-500 ${
                  openIndex === index
                    ? 'border-amiva-primary shadow-2xl scale-[1.02]'
                    : hoveredIndex === index
                      ? 'border-gray-300 shadow-xl'
                      : 'border-gray-200 shadow-lg hover:shadow-xl'
                }`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <button
                  onClick={() => toggleQuestion(index)}
                  className="w-full px-8 py-6 text-left flex items-start justify-between focus:outline-none focus:ring-2 focus:ring-amiva-primary focus:ring-offset-2 rounded-2xl transition-colors duration-300 hover:bg-gray-50/50"
                >
                  <div className="flex items-start">
                    <span
                      className={`mr-4 text-2xl font-bold flex-shrink-0 transition-all duration-300 ${
                        openIndex === index
                          ? 'text-amiva-accent animate-pulse'
                          : 'text-amiva-primary'
                      }`}
                    >
                      {openIndex === index ? 'A' : 'Q'}:
                    </span>
                    <h3
                      className={`text-xl font-bold pr-8 transition-colors duration-300 ${
                        openIndex === index ? 'text-amiva-primary' : 'text-gray-900'
                      }`}
                    >
                      {faq.question}
                    </h3>
                  </div>
                  <div className="relative">
                    <svg
                      className={`w-6 h-6 transform transition-all duration-500 flex-shrink-0 ${
                        openIndex === index
                          ? 'rotate-180 text-amiva-primary'
                          : 'text-gray-400 group-hover:text-gray-600'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    {openIndex === index && (
                      <div className="absolute inset-0 w-6 h-6 bg-amiva-primary/20 rounded-full animate-ping"></div>
                    )}
                  </div>
                </button>
                <div
                  ref={(el) => (contentRefs.current[index] = el)}
                  className="transition-all duration-500 ease-in-out"
                  style={{
                    maxHeight: openIndex === index ? `${heights[index]}px` : '0',
                    opacity: openIndex === index ? 1 : 0,
                    transform: openIndex === index ? 'translateY(0)' : 'translateY(-10px)',
                  }}
                >
                  <div className="px-8 pb-6">
                    <div
                      className={`bg-gradient-to-r from-amiva-primary/5 to-amiva-accent/5 rounded-xl p-6 transition-all duration-500 ${
                        openIndex === index
                          ? 'translate-x-0 opacity-100'
                          : 'translate-x-4 opacity-0'
                      }`}
                    >
                      <p className="text-gray-700 leading-relaxed">
                        <span className="text-amiva-accent font-bold text-lg mr-2">→</span>
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Questions CTA */}
          <div className="mt-20 text-center bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12 border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Have a Question We Didn't Answer?
            </h3>
            <p className="text-lg text-gray-600 mb-8">
              Let's have a confidential conversation about your specific situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://app.usemotion.com/meet/mike-pantouvakis/acquisition-discussion"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-block"
              >
                Ask Mike Directly
              </a>
              <a href={`mailto:${require('../..//config/appConfig').appConfig.site.contactEmail}`} className="btn-secondary inline-block">
                Send Us an Email
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Related Resources */}
      <div className="bg-gradient-to-br from-gray-50 to-white py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Ready to Learn More?</h2>
            <p className="text-xl text-gray-600">
              Explore these resources to better understand our process and what to expect.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <a href="/valuation" className="group">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="w-16 h-16 bg-amiva-gradient rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                    <path
                      fillRule="evenodd"
                      d="M4 5a2 2 0 012-2 1 1 0 000 2H6a2 2 0 00-2 2v6a2 2 0 002 2h2a1 1 0 100 2H6a4 4 0 01-4-4V5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-amiva-primary transition-colors">
                  Get Your Valuation
                </h3>
                <p className="text-gray-600 mb-4">
                  Discover what your business is worth in today's market with our free valuation
                  tool.
                </p>
                <span className="text-amiva-primary font-semibold flex items-center">
                  Start Valuation
                  <svg
                    className="w-4 h-4 ml-2 transform group-hover:translate-x-2 transition-transform"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </div>
            </a>

            <a href="/process" className="group">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="w-16 h-16 bg-amiva-gradient rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-amiva-primary transition-colors">
                  Our Process
                </h3>
                <p className="text-gray-600 mb-4">
                  Learn about our proven 6-month process for maximizing your business value.
                </p>
                <span className="text-amiva-primary font-semibold flex items-center">
                  View Process
                  <svg
                    className="w-4 h-4 ml-2 transform group-hover:translate-x-2 transition-transform"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </div>
            </a>

            <a href="/success-stories" className="group">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="w-16 h-16 bg-amiva-gradient rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-amiva-primary transition-colors">
                  Success Stories
                </h3>
                <p className="text-gray-600 mb-4">
                  Read how we've helped founders achieve life-changing exits.
                </p>
                <span className="text-amiva-primary font-semibold flex items-center">
                  Read Stories
                  <svg
                    className="w-4 h-4 ml-2 transform group-hover:translate-x-2 transition-transform"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
